/**
 * File: csv.ts
 * Path: packages/config/src/accounting/csv.ts
 * Description: Reads a CSV file (a Zoho report export now, bank statements
 * next) into rows of cells. Handles
 * quoted cells with commas, quotes and line breaks in them, a byte-order
 * mark, and files that use semicolons or tabs instead of commas.
 */

const detectDelimiter = (text: string): string => {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  const counts = [",", ";", "\t"].map((d) => ({
    d,
    n: firstLine.split(d).length - 1,
  }));
  counts.sort((a, b) => b.n - a.n);
  return counts[0].n > 0 ? counts[0].d : ",";
};

/**
 * Every row of the file as trimmed cells. Blank rows are dropped unless
 * `keepBlank`, which keeps row numbers equal to the file's line numbers.
 */
export const parseCsv = (input: string, keepBlank = false): string[][] => {
  const text = input.replace(/^\uFEFF/, "");
  const delimiter = detectDelimiter(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"') {
      quoted = true;
    } else if (ch === delimiter) {
      row.push(cell.trim());
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      row.push(cell.trim());
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }
  row.push(cell.trim());
  rows.push(row);
  if (text.endsWith("\n")) rows.pop();
  return keepBlank ? rows : rows.filter((r) => r.some((c) => c !== ""));
};
